import { toValue } from "vue";
import type { MaybeRefOrGetter } from "vue";
import type { CanvasDimensions } from "./CanvasDimensions";

export function useNetCoords(data: {
  canvasDimensions: MaybeRefOrGetter<CanvasDimensions | undefined>;
  gridSize?: MaybeRefOrGetter<number>;
}) {
  /**
   * Converts a point from global coordinates to local coordinates
   * relative to the canvas, taking into account the canvas dimensions,
   * translation, and scaling.
   */
  function toLocal(point: { x: number; y: number }) {
    const canvas = toValue(data.canvasDimensions);
    const GRID_SIZE = toValue(data.gridSize) ?? 1;

    if (canvas == null) return { x: 0, y: 0 };

    const x =
      Math.round(
        (point.x -
          canvas.containerX -
          canvas.translateX * canvas.scale +
          canvas.width / 2 -
          canvas.containerWidth / 2) /
          canvas.scale /
          GRID_SIZE
      ) * GRID_SIZE;
    const y =
      Math.round(
        (point.y -
          canvas.containerY -
          canvas.translateY * canvas.scale +
          canvas.height / 2 -
          canvas.containerHeight / 2) /
          canvas.scale /
          GRID_SIZE
      ) * GRID_SIZE;

    return { x, y };
  }

  function toLocalSize(size: number) {
    const canvas = toValue(data.canvasDimensions);
    
    if (canvas == null) return 0;
    return Math.round(size / canvas.scale);
  }

  // function toCanvas(point: { x: number; y: number }) {
  //   const canvas = state.canvas;

  //   if (canvas == null) return { x: 0, y: 0 };

  //   const x =
  //     (point.x -
  //       canvas.containerX -
  //       canvas.translateX * canvas.scale +
  //       canvas.width / 2 -
  //       canvas.containerWidth / 2) /
  //     canvas.scale;
  //   const y =
  //     (point.y -
  //       canvas.containerY -
  //       canvas.translateY * canvas.scale +
  //       canvas.height / 2 -
  //       canvas.containerHeight / 2) /
  //     canvas.scale;
  //   return { x, y };
  // }

  function toViewport(point: { x: number; y: number }) {
    const canvas = toValue(data.canvasDimensions);

    if (canvas == null) return { x: 0, y: 0 };

    const x =
      point.x * canvas.scale +
      canvas.translateX * canvas.scale -
      canvas.width / 2 +
      canvas.containerWidth / 2;
    const y =
      point.y * canvas.scale +
      canvas.translateY * canvas.scale -
      canvas.height / 2 +
      canvas.containerHeight / 2;

    return { x, y };
  }

  function toGlobal(point: { x: number; y: number }) {
    const canvas = toValue(data.canvasDimensions);

    if (canvas == null) return { x: 0, y: 0 };

    const x =
      point.x * canvas.scale +
      canvas.containerX +
      canvas.translateX * canvas.scale -
      canvas.width / 2 +
      canvas.containerWidth / 2;
    const y =
      point.y * canvas.scale +
      canvas.containerY +
      canvas.translateY * canvas.scale -
      canvas.height / 2 +
      canvas.containerHeight / 2;

    return { x, y };
  }

  function toLocalRect(rect: Rect) {
    const leftTop = toLocal({
      x: rect.left,
      y: rect.top
    });
    const bottomRight = toLocal({
      x: rect.right,
      y: rect.bottom
    });

    const left = leftTop.x;
    const top = leftTop.y;
    const right = bottomRight.x;
    const bottom = bottomRight.y;
    const width = right - left;
    const height = bottom - top;
    const newRect = {
      x: left,
      y: top,
      left,
      top,
      right,
      bottom,
      width,
      height
    };
    return newRect;
  }

  function toGlobalRect(rect: Rect) {
    const leftTop = toGlobal({
      x: rect.left,
      y: rect.top
    });
    const bottomRight = toGlobal({
      x: rect.right,
      y: rect.bottom
    });

    const left = leftTop.x;
    const top = leftTop.y;
    const right = bottomRight.x;
    const bottom = bottomRight.y;
    const width = right - left;
    const height = bottom - top;
    const newRect = {
      x: left,
      y: top,
      left,
      top,
      right,
      bottom,
      width,
      height
    };
    return newRect;
  }

  return { toLocal, toLocalRect, toGlobal, toGlobalRect, toViewport, toLocalSize };
}

interface Rect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width?: number;
  height?: number;
}
